import React, { useState, useEffect, useContext } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Container } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, LightMode as SunIcon, DarkMode as MoonIcon } from '@mui/icons-material';
import { ColorModeContext } from '../App';

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { toggleColorMode, mode } = useContext(ColorModeContext);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      // Set scroll state
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Calculate scroll progress
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScroll > 0) {
        setScrollProgress((window.scrollY / totalScroll) * 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navLinks = [
    { title: 'Blog', path: '/blogs' },
    { title: 'Tools', path: '/tools' },
    { title: 'Services', path: '/services' },
    { title: 'About', path: '/about' },
  ];

  const drawer = (
    <Box
      sx={{ 
        width: 280, 
        pt: 4, 
        px: 2, 
        height: '100%',
        bgcolor: mode === 'light' ? '#F8FAFC' : '#0B0F19',
        color: 'text.primary'
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, px: 2, mb: 3, fontFamily: '"Outfit", sans-serif' }}>
        CoreTechTalks
      </Typography>
      <List>
        {navLinks.map((link) => (
          <ListItem key={link.title} disablePadding sx={{ mb: 1 }}>
            <ListItemButton 
              component={Link} 
              to={link.path}
              sx={{
                borderRadius: '8px',
                bgcolor: location.pathname === link.path ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                color: location.pathname === link.path ? 'primary.main' : 'text.primary',
                '&:hover': {
                  bgcolor: 'rgba(99, 102, 241, 0.04)',
                }
              }}
            >
              <ListItemText 
                primary={link.title} 
                primaryTypographyProps={{ fontWeight: 600, fontSize: '15px' }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding sx={{ mt: 4 }}>
          <ListItemButton component={Link} to="/contact" sx={{ p: 0 }}>
            <Button variant="contained" color="primary" fullWidth sx={{ py: 1.2 }}>Contact</Button>
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: scrolled 
            ? (mode === 'light' ? 'rgba(248, 250, 252, 0.75)' : 'rgba(8, 11, 16, 0.75)')
            : 'transparent',
          backdropFilter: 'blur(16px)', 
          borderBottom: scrolled
            ? `1px solid ${mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.06)'}`
            : '1px solid transparent',
          top: 0,
          zIndex: 1100,
          transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
          boxShadow: scrolled
            ? (mode === 'light' ? '0 4px 20px -5px rgba(0, 0, 0, 0.03)' : '0 4px 30px -5px rgba(0, 0, 0, 0.15)')
            : 'none'
        }}
      >
        {/* Scroll Progress Bar */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            height: '3px', 
            width: `${scrollProgress}%`, 
            background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
            transition: 'width 0.1s ease-out',
            zIndex: 1200
          }} 
        />
        
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: '64px', md: '72px' } }}>
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                textDecoration: 'none',
                color: 'text.primary',
                fontWeight: 800,
                fontFamily: '"Outfit", sans-serif',
                fontSize: '22px',
                letterSpacing: '-0.02em',
                display: 'flex',
                alignItems: 'center',
                '& span': {
                  background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginRight: '2px'
                }
              }}
            >
              <span>Core</span>TechTalks
            </Typography>
            
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Button
                    component={Link}
                    to={link.path}
                    key={link.title}
                    sx={{
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontWeight: 600,
                      fontSize: '15px',
                      ml: 1,
                      px: 2,
                      py: 1,
                      position: 'relative',
                      borderRadius: '8px',
                      '&:hover': {
                        color: 'text.primary',
                        backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
                      },
                      '&::after': isActive ? {
                        content: '""',
                        position: 'absolute',
                        bottom: '4px',
                        left: '16px',
                        right: '16px',
                        height: '2px',
                        borderRadius: '99px',
                        background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                      } : {}
                    }}
                  >
                    {link.title}
                  </Button>
                );
              })}
              
              <DividerOrientationWrapper mode={mode} />

              <IconButton 
                onClick={toggleColorMode} 
                color="inherit" 
                sx={{ 
                  ml: 1, 
                  mr: 2,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', transform: 'rotate(15deg)' },
                  transition: 'transform 0.2s ease-in-out'
                }}
              >
                {mode === 'dark' ? <SunIcon sx={{ fontSize: '20px' }} /> : <MoonIcon sx={{ fontSize: '20px' }} />}
              </IconButton>

              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/contact"
                sx={{ 
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                  px: 3
                }}
              >
                Contact
              </Button>
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
              <IconButton 
                onClick={toggleColorMode} 
                color="inherit" 
                sx={{ 
                  mr: 1,
                  color: 'text.secondary'
                }}
              >
                {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
              </IconButton>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            border: 'none',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)'
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

const DividerOrientationWrapper = ({ mode }) => (
  <Box 
    sx={{ 
      height: '20px', 
      width: '1px', 
      bgcolor: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)', 
      mx: 2 
    }} 
  />
);

export default Navbar;
